/**
 * Per-clinician Exam2 layout prefs (localStorage, not PHI): section order, labels,
 * visibility, finding options, and preferred normals — scoped by specialty × setting × note type.
 *
 * Preferred normals also still write through exam2Defaults.js for backward compatibility
 * with the older uid-only key.
 */

import {
  loadExam2PreferredNormals,
  setExam2PreferredNormal,
} from "./exam2Defaults";
import { normalizeExam2Values } from "./exam2Catalog";

const uniqTrimmed = (items) => [
  ...new Set(
    (Array.isArray(items) ? items : [])
      .map((item) => String(item || "").trim())
      .filter(Boolean),
  ),
];

const scopePart = (value, fallback) => {
  const text = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-|-$/g, "");
  return text || fallback;
};

/**
 * @param {{ specialty?: string, setting?: string, noteType?: string, inpatient?: boolean, noteFormat?: string }} scope
 */
export function resolveExam2NoteType(scope = {}) {
  if (scope.noteType) return scopePart(scope.noteType, "outpatient");
  const setting = String(scope.setting || "").toLowerCase();
  const inpatient = Boolean(scope.inpatient) || setting === "inpatient";
  if (!inpatient) return "outpatient";
  const format = String(scope.noteFormat || "").toLowerCase();
  if (format === "progress" || format === "events" || format === "admission") return format;
  return "admission";
}

export function exam2LayoutStorageKey(userId, scope = {}) {
  const uid = String(userId || "").trim() || "anonymous";
  const specialty = scopePart(scope.specialty, "general");
  const setting = scopePart(scope.setting || (scope.inpatient ? "inpatient" : "outpatient"), "outpatient");
  const noteType = resolveExam2NoteType(scope);
  return `chartmind:exam2-layout:${uid}:${specialty}:${setting}:${noteType}`;
}

/**
 * @returns {{ order: string[], systems: Record<string, {
 *   name?: string,
 *   hidden?: boolean,
 *   options?: string[],
 *   normalOptions?: string[],
 *   preferredNormal?: string,
 * }> }}
 */
export function emptyExam2LayoutPrefs() {
  return { order: [], systems: {} };
}

export function loadExam2LayoutPrefs(userId, scope = {}) {
  if (typeof window === "undefined") return emptyExam2LayoutPrefs();
  try {
    const raw = window.localStorage.getItem(exam2LayoutStorageKey(userId, scope));
    if (!raw) {
      // Fall back: lift legacy preferred normals into an empty layout shell.
      const legacy = loadExam2PreferredNormals(userId);
      if (!Object.keys(legacy).length) return emptyExam2LayoutPrefs();
      return {
        order: [],
        systems: Object.fromEntries(
          Object.entries(legacy).map(([id, preferredNormal]) => [
            id,
            { preferredNormal: String(preferredNormal || "").trim() },
          ]),
        ),
      };
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return emptyExam2LayoutPrefs();
    }
    const order = Array.isArray(parsed.order)
      ? parsed.order.map((id) => String(id || "").trim()).filter(Boolean)
      : [];
    const systems = {};
    const rawSystems =
      parsed.systems && typeof parsed.systems === "object" && !Array.isArray(parsed.systems)
        ? parsed.systems
        : {};
    Object.entries(rawSystems).forEach(([id, entry]) => {
      if (!id || !entry || typeof entry !== "object") return;
      systems[String(id)] = {
        name: entry.name != null ? String(entry.name) : undefined,
        hidden: Boolean(entry.hidden),
        options: uniqTrimmed(entry.options),
        normalOptions: uniqTrimmed(entry.normalOptions),
        preferredNormal: String(entry.preferredNormal || "").trim() || undefined,
      };
    });
    // Merge any legacy preferred normals not yet stored in this scope.
    const legacy = loadExam2PreferredNormals(userId);
    Object.entries(legacy).forEach(([id, preferredNormal]) => {
      const text = String(preferredNormal || "").trim();
      if (!text) return;
      if (!systems[id]) systems[id] = { preferredNormal: text };
      else if (!systems[id].preferredNormal) systems[id].preferredNormal = text;
    });
    return { order, systems };
  } catch {
    return emptyExam2LayoutPrefs();
  }
}

export function saveExam2LayoutPrefs(userId, scope, prefs) {
  if (typeof window === "undefined") return;
  try {
    const order = Array.isArray(prefs?.order)
      ? prefs.order.map((id) => String(id || "").trim()).filter(Boolean)
      : [];
    const systems = {};
    Object.entries(prefs?.systems || {}).forEach(([id, entry]) => {
      if (!id || !entry) return;
      const preferredNormal = String(entry.preferredNormal || "").trim();
      systems[id] = {
        ...(entry.name != null ? { name: String(entry.name) } : {}),
        hidden: Boolean(entry.hidden),
        options: uniqTrimmed(entry.options),
        normalOptions: uniqTrimmed(entry.normalOptions),
        ...(preferredNormal ? { preferredNormal } : {}),
      };
    });
    window.localStorage.setItem(
      exam2LayoutStorageKey(userId, scope),
      JSON.stringify({ order, systems }),
    );
    // Keep the legacy uid-only map in sync so star/Tab still work if layout key is missing.
    Object.entries(systems).forEach(([id, entry]) => {
      if (entry.preferredNormal) setExam2PreferredNormal(userId, id, entry.preferredNormal);
    });
  } catch {
    // Quota / private mode — ignore.
  }
}

/**
 * Build prefs snapshot from the current systems list + preferred map.
 */
export function systemsToExam2LayoutPrefs(systems = [], preferredBySystemId = {}) {
  const list = Array.isArray(systems) ? systems : [];
  const order = list.map((row) => String(row?.id || "").trim()).filter(Boolean);
  const nextSystems = {};
  list.forEach((row) => {
    const id = String(row?.id || "").trim();
    if (!id) return;
    const preferredNormal =
      String(preferredBySystemId?.[id] || row.preferredNormal || "").trim() || undefined;
    nextSystems[id] = {
      name: String(row.name || "").trim() || undefined,
      hidden: Boolean(row.hidden),
      options: uniqTrimmed(row.options),
      normalOptions: uniqTrimmed(row.normalOptions),
      ...(preferredNormal ? { preferredNormal } : {}),
    };
  });
  return { order, systems: nextSystems };
}

/**
 * Apply saved layout to a systems array. Preserves values; order/name/hidden/options from prefs.
 * Systems in prefs.order come first; any remaining systems append in their prior relative order.
 */
export function applyExam2LayoutPrefs(systems = [], prefs = emptyExam2LayoutPrefs()) {
  const list = Array.isArray(systems) ? systems.map((row) => ({ ...row })) : [];
  const byId = new Map(list.map((row) => [row.id, row]));
  const prefSystems = prefs?.systems && typeof prefs.systems === "object" ? prefs.systems : {};
  const order = Array.isArray(prefs?.order) ? prefs.order : [];

  const applyEntry = (row) => {
    const entry = prefSystems[row.id];
    if (!entry) return { ...row, hidden: Boolean(row.hidden) };
    const options = entry.options?.length ? uniqTrimmed(entry.options) : uniqTrimmed(row.options);
    const normalOptions = entry.normalOptions?.length
      ? uniqTrimmed(entry.normalOptions)
      : uniqTrimmed(row.normalOptions);
    // Ensure preferred normal remains selectable.
    const preferred = String(entry.preferredNormal || "").trim();
    const withPreferred = preferred && !options.includes(preferred) ? [...options, preferred] : options;
    const normalsWithPreferred =
      preferred && !normalOptions.includes(preferred)
        ? [...normalOptions, preferred]
        : normalOptions;
    return {
      ...row,
      name: entry.name != null && String(entry.name).trim() ? String(entry.name).trim() : row.name,
      hidden: Boolean(entry.hidden),
      options: withPreferred,
      normalOptions: normalsWithPreferred,
      value: normalizeExam2Values(row.value),
    };
  };

  const used = new Set();
  const ordered = [];
  order.forEach((id) => {
    const row = byId.get(id);
    if (!row || used.has(id)) return;
    ordered.push(applyEntry(row));
    used.add(id);
  });
  list.forEach((row) => {
    if (!row?.id || used.has(row.id)) return;
    ordered.push(applyEntry(row));
    used.add(row.id);
  });
  return ordered;
}

/**
 * Preferred-normal map derived from layout prefs (for Tab fill / stars).
 */
export function preferredNormalsFromLayoutPrefs(prefs = emptyExam2LayoutPrefs()) {
  const out = {};
  Object.entries(prefs?.systems || {}).forEach(([id, entry]) => {
    const text = String(entry?.preferredNormal || "").trim();
    if (id && text) out[id] = text;
  });
  return out;
}
